/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice\src\Content\Car\CarTemplateForm\CarTemplateForm.tsx
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice
 * Created Date: Friday August 9th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Friday August 9th 2024 11:09:22 am
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import CarService from "Services/Car/CarService";
import CarSelect from "../CarSelect/CarSelect";
import translator from "MultiLanguage/TranslationWrapper";
import { TextField } from "@mui/material";
import { TemplateCarDataType } from "Type/Car.type";
import Config from "Config/Config";

type building = {
    [valName: string]: [string, Function]
};

type CarTemplateFormProps = {
    cb: Function
}

const CarTemplateForm = forwardRef((props: CarTemplateFormProps, ref) => {
    const brand = useState("");
    const model = useState("");
    const version = useState("");
    const color = useState("");
    const comments = useState("");
    const [customText, setCustomText] = useState("");
    const gears = Config.COMMON.GEARS;
    const [categories, setCategories] = useState<{id: string, Name: string}[]>([]);
    const [energy, setEnergy] = useState<{id: string, Name: string}[]>([]);
    const [carType, setCarType] = useState<{id: string, Name: string}[]>([]);
    const [subSection, setSubSection] = useState({});
    const [up, setUp] = useState(false);
    const carData = useRef<TemplateCarDataType>({
        brand: "",
        model: "",
        version: "",
        color: "",
        customText: "",
        energy: "",
        type: "",
        subSection: "",
        category: ""
    });

    useImperativeHandle(ref, () => {
        return carData.current;
    }, [brand[0], model[0], version[0], color[0], customText, up]);

    useEffect(() => {
        CarService.templateData().then(result => {
            setEnergy(result.data[0]);
            setCarType(result.data[1]);
            setCategories(result.data[2]);
            setSubSection(result.data[3]);
        }).catch(error => {
            console.log(error.message);
        });
    }, []);

    const handleOnChange = (updateKey: string, value: string, setter: Function) => {
        setter(value);
        carData.current = {
            ...carData.current,
            [updateKey]: value ?? undefined,
        };
    };

    const manageSelectValue = (updateKey: string, value: string) => {
        let tmp: string | undefined = undefined;
        if (updateKey == "energy")
            tmp = energy.filter(x => x.Name == value.toLowerCase())[0]?.id;
        else if (updateKey == "type")
            tmp = carType.filter(x => x.Name == value.toLowerCase())[0]?.id;
        else if (updateKey == "category")
            tmp = categories.filter(x => x.Name.toLowerCase() == value.toLowerCase())[0]?.id;

        carData.current = {
            ...carData.current,
            [updateKey]: tmp ?? value
        };
        setUp(!up);
        props.cb();
    };

    const buildInputs = (input: building) => {
        let myInputs: JSX.Element[] = [];
        Object.entries(input).forEach(val => {
            myInputs.push(<TextField required key={val[0]} size="small" label={translator.decode(`dynamic.${val[0]}`)} value={val[1][0]} onChange={(e) => {handleOnChange(val[0], e.target.value, val[1][1]); props.cb()} } />);
        });
        return myInputs;
    };

    const myDataLeft = useMemo(() => {
        const basicData = {"brand": brand, "model": model, "version": version, "color": color, "comments": comments};
        return buildInputs(basicData);
    }, [brand[0], model[0], version[0], color[0], comments[0]]);

    return (
        <div className="d-flex gap-3">
            <div className="d-flex gap-3 flex-column w-100">
                {myDataLeft}
                <TextField required key={"customText"} size="small" inputProps={{ maxLength: 16 }} label={translator.decode(`dynamic.${"customText"}`)} value={customText} onChange={(e) => {handleOnChange("customText", e.target.value, setCustomText); props.cb()}} />
            </div>
            <div className="d-flex gap-3 flex-column mb-3 w-100">
                <CarSelect label={translator.decode("dynamic.energy")} item={""} updateKey={"energy"} optionsList={energy.map(x => x.Name)} keyTrad={"energyType"} disabled={true} updateCallBack={manageSelectValue} />
                <CarSelect label={translator.decode("form.placeholder.gears")} item={""} updateKey={"gears"} optionsList={gears} keyTrad={"form.placeholder"} disabled={true} updateCallBack={manageSelectValue} />
                <CarSelect label={translator.decode("car.Type")} item={""} updateKey={"type"} optionsList={carType.map(x => x.Name)} keyTrad={""} disabled={true} updateCallBack={manageSelectValue} />
                <CarSelect label={translator.decode("car.SubSection")} item={""} updateKey={"subSection"} optionsList={Object.keys(subSection).map(x => x.toLowerCase())} keyTrad={"carType"} disabled={true} updateCallBack={manageSelectValue} />
                <CarSelect label={translator.decode("car.Category")} item={""} updateKey={"category"} optionsList={categories.map(x => x.Name)} keyTrad={""} disabled={true} updateCallBack={manageSelectValue} />
            </div>
        </div>
    );
});

export default CarTemplateForm;
